/**
 * Card de agendamento — visual design system.
 *
 * Compõe atoms/molecules do DS (Card, Badge, Switch, IconButton, Icon,
 * Tooltip). Sem emojis, sem SVG inline, sem classes hardcoded — cores e
 * sombras via tokens.
 */

import cronstrue from 'cronstrue/i18n'

import { Icon, type IconName } from '@/design-system/atoms/Icon'
import { IconButton } from '@/design-system/atoms/IconButton'
import { Badge } from '@/design-system/atoms/Badge'
import { Switch } from '@/design-system/atoms/Switch'
import { Card } from '@/design-system/molecules/Card'
import { Tooltip } from '@/design-system/molecules/Tooltip'

import type { ScheduledTask, ScheduledTaskStatus } from '../../types/scheduler'

const TOOL_LABELS: Record<string, string> = {
  list_open_os: 'OS em aberto',
  list_open_hd: 'HDs em aberto',
  cross_reference_os_hd: 'Cruzamento OS × HD',
}

const TOOL_ICONS: Record<string, IconName> = {
  list_open_os: 'file-text',
  list_open_hd: 'ticket',
  cross_reference_os_hd: 'pie-chart',
}

const STATUS_BADGE: Record<
  ScheduledTaskStatus,
  { label: string; variant: 'success' | 'error' | 'warning' | 'info' }
> = {
  success: { label: 'Sucesso', variant: 'success' },
  failed: { label: 'Falha', variant: 'error' },
  partial: { label: 'Parcial', variant: 'warning' },
  running: { label: 'Executando', variant: 'info' },
}

function describeRecurrence(task: ScheduledTask): string {
  if (task.run_at) {
    try {
      const d = new Date(task.run_at)
      const formatted = new Intl.DateTimeFormat('pt-BR', {
        dateStyle: 'long',
        timeStyle: 'short',
      }).format(d)
      return `Única vez — ${formatted}`
    } catch {
      return `Única vez — ${task.run_at}`
    }
  }
  try {
    return cronstrue.toString(task.cron_expression, {
      locale: 'pt_BR',
      use24HourTimeFormat: true,
    })
  } catch {
    return task.cron_expression
  }
}

function formatDateTime(value: string | null): string {
  if (!value) return '—'
  try {
    return new Intl.DateTimeFormat('pt-BR', {
      dateStyle: 'short',
      timeStyle: 'short',
    }).format(new Date(value))
  } catch {
    return value
  }
}

interface Props {
  task: ScheduledTask
  onToggle: () => void
  onEdit: () => void
  onDelete: () => void
  onRunNow: () => void
  onViewHistory: () => void
}

export default function ScheduledTaskCard({
  task,
  onToggle,
  onEdit,
  onDelete,
  onRunNow,
  onViewHistory,
}: Props) {
  const lastStatus = task.last_execution?.status
  const statusBadge = lastStatus ? STATUS_BADGE[lastStatus] : null
  const toolIcon = TOOL_ICONS[task.tool_name] ?? 'settings'

  return (
    <Card
      padding="lg"
      radius="2xl"
      className={task.is_active ? '' : 'opacity-70'}
    >
      {/* Header: ícone + título + switch */}
      <div className="mb-4 flex items-start gap-3">
        <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-primary-50 text-primary-600 dark:bg-primary-900/30 dark:text-primary-400">
          <Icon name={toolIcon} size={20} />
        </div>
        <div className="min-w-0 flex-1">
          <h3 className="truncate font-semibold text-gray-900 dark:text-white">
            {task.name}
          </h3>
          <p className="mt-0.5 text-xs text-gray-500 dark:text-gray-400">
            {TOOL_LABELS[task.tool_name] ?? task.tool_name}
          </p>
        </div>
        <Switch
          checked={task.is_active}
          onChange={onToggle}
          aria-label={
            task.is_active ? 'Desativar agendamento' : 'Ativar agendamento'
          }
        />
      </div>

      {/* Recorrência */}
      <div className="mb-3 flex items-center gap-2 text-sm text-gray-600 dark:text-gray-300">
        <Icon
          name={task.run_at ? 'calendar-days' : 'clock'}
          size={16}
          className="text-gray-400 dark:text-gray-500"
        />
        <span className="truncate">{describeRecurrence(task)}</span>
      </div>

      {/* Status da última execução */}
      <div className="mb-4 flex items-center gap-2">
        {statusBadge ? (
          <Badge variant={statusBadge.variant} size="sm" shape="pill">
            Última: {statusBadge.label.toLowerCase()}
          </Badge>
        ) : (
          <Badge variant="default" size="sm" shape="pill">
            Nunca executada
          </Badge>
        )}
        {task.last_run_at && (
          <span className="text-xs text-gray-500 dark:text-gray-400">
            {formatDateTime(task.last_run_at)}
          </span>
        )}
      </div>

      {/* Próxima execução + canais */}
      <div className="mb-4 flex items-center justify-between text-xs text-gray-500 dark:text-gray-400">
        <span>
          Próxima:{' '}
          <strong className="text-gray-700 dark:text-gray-300">
            {formatDateTime(task.next_run_at)}
          </strong>
        </span>
        <div className="flex gap-1.5">
          {task.delivery_channels.includes('email') && (
            <Tooltip content="E-mail">
              <Badge variant="info" size="sm" shape="pill">
                <Icon name="mail" size={12} />
              </Badge>
            </Tooltip>
          )}
          {task.delivery_channels.includes('whatsapp') && (
            <Tooltip content="WhatsApp">
              <Badge variant="success" size="sm" shape="pill">
                <Icon name="chat" size={12} />
              </Badge>
            </Tooltip>
          )}
        </div>
      </div>

      {/* Ações */}
      <div className="flex items-center justify-end gap-1 border-t border-gray-100 pt-3 dark:border-gray-800">
        <Tooltip content="Executar agora">
          <IconButton
            variant="brand"
            size="sm"
            onClick={onRunNow}
            aria-label="Executar agora"
          >
            <Icon name="send" size={16} />
          </IconButton>
        </Tooltip>
        <Tooltip content="Ver histórico">
          <IconButton
            variant="ghost"
            size="sm"
            onClick={onViewHistory}
            aria-label="Ver histórico"
          >
            <Icon name="list" size={16} />
          </IconButton>
        </Tooltip>
        <Tooltip content="Editar">
          <IconButton
            variant="ghost"
            size="sm"
            onClick={onEdit}
            aria-label="Editar agendamento"
          >
            <Icon name="pencil" size={16} />
          </IconButton>
        </Tooltip>
        <Tooltip content="Excluir">
          <IconButton
            variant="ghost"
            size="sm"
            onClick={onDelete}
            aria-label="Excluir agendamento"
            className="text-red-500 hover:bg-red-50 hover:text-red-600 dark:hover:bg-red-950/30"
          >
            <Icon name="trash" size={16} />
          </IconButton>
        </Tooltip>
      </div>
    </Card>
  )
}
